/**
 * /policies/travel/categories — Session 3c PR 21.
 * Owning agent: Mutaba'aa.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { TravelCategoryItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function TravelCategoriesPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-travel-categories", le],
    queryFn: () => session3cApi.listTravelCategories(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<TravelCategoryItem>[] = [
    {
      headerKey: "policies.travel_categories.col.name",
      label: "Category",
      render: (r) => <div><div className="font-medium">{r.category_name}</div><div className="text-[10px] text-text-tertiary">{r.description}</div></div>,
    },
    {
      headerKey: "policies.travel_categories.col.grades",
      label: "Eligible grades",
      render: (r) => <span className="text-xs text-text-secondary">{r.eligible_grades.join(" · ")}</span>,
    },
    {
      headerKey: "policies.travel_categories.col.class",
      label: "Default class",
      render: (r) => <span className="text-xs uppercase">{r.booking_class_default}</span>,
    },
    {
      headerKey: "policies.travel_categories.col.tier",
      label: "Per-diem tier",
      render: (r) => <span className="font-mono text-xs">{r.per_diem_tier}</span>,
    },
  ];

  return (
    <PolicyListPage<TravelCategoryItem>
      title={t("policies.travel_categories.title", "Travel Categories")}
      subtitle={t("policies.travel_categories.subtitle", "CEO · Director · Manager · Supervisor band definitions with per-band travel rules.")}
      agent="mutabaa"
      domain="travel_categories"
      newButtonLabel={t("policies.travel_categories.cta.new", "New Travel Category")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.travel_categories.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.travel_categories.suggestion.clean_body", "Categories drive booking-class defaults + per-diem tier selection on every travel request."),
          primaryActionLabel: t("policies.travel_categories.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "travel-categories-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.travel_categories.title", "Travel Categories"),
        window_label: t("policies.travel_categories.window", "across grades"),
      }}
    />
  );
}
